// In-app help content for LogiOS eCom.
//
// Each section carries a `roles` array — the drawer shows a section only if the
// signed-in user's role is listed (sections with no `roles` are shown to
// everyone). This mirrors the app's RBAC: a Warehouse Operator sees Welcome +
// Warehouse + How-To + FAQ; an Operator Admin sees everything.
//
// Icons are Font Awesome class strings (the app loads FA via CSS, not the React
// package). Topic `body` is trusted HTML rendered into the content panel.

import { ROLES } from '../../config/roles.js';

const { OPERATOR_ADMIN, WAREHOUSE_OPERATOR, FINANCE_USER, MERCHANT_USER, REGIONAL_ADMIN, CROSS_BORDER_OPS } = ROLES;

export const helpSections = [
  {
    id: 'welcome',
    title: 'Welcome',
    icon: 'fas fa-rocket',
    roles: null, // everyone
    topics: [
      {
        id: 'getting-started',
        title: 'Getting started',
        body: `
          <p><strong>LogiOS eCom</strong> is a unified 3PL &amp; cross-border fulfilment platform — it connects your marketplace orders to warehouse operations, courier delivery, COD reconciliation, and BIR-compliant billing in one place.</p>
          <p style="margin-top:8px;">When you sign in you land on the dashboard built for your role. Use the top navigation to move between modules — you only ever see the modules your role is permitted to use.</p>
          <p style="margin-top:12px;"><strong>The big picture:</strong></p>
          <ul>
            <li><strong>Orders</strong> flow in from marketplaces (Shopee, Lazada, TikTok, Shopify).</li>
            <li><strong>Warehouse</strong> receives stock, then picks, packs, and ships each order.</li>
            <li><strong>Couriers</strong> deliver and remit COD cash back to you.</li>
            <li><strong>Finance</strong> reconciles that cash and issues invoices.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: the <strong>?</strong> button (top-right) opens this help on the section most relevant to the page you're on.</p>
        `,
      },
      {
        id: 'roles-access',
        title: 'Your role & access',
        body: `
          <p>LogiOS is strictly role-based. Your account has one role, and it determines which pages, menus, and data you can see.</p>
          <ul>
            <li><strong>Operator Admin</strong> — full platform access and configuration.</li>
            <li><strong>Warehouse Operator</strong> — receiving, putaway, picking, packing, inventory.</li>
            <li><strong>Finance User</strong> — COD reconciliation, billing, e-invoicing, claims.</li>
            <li><strong>Merchant User</strong> — your own store's orders, COD, and analytics (tenant-isolated).</li>
            <li><strong>Regional Admin</strong> — country operational profiles across SEA markets.</li>
            <li><strong>Cross-Border Ops</strong> — international shipment exceptions and customs.</li>
          </ul>
          <p style="margin-top:8px;">The navigation only lists what you can open. If you follow an old link to a page outside your role, the app quietly returns you to your own dashboard rather than showing an error.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">You can review your identity, role, and tenant under the avatar menu → <strong>Profile</strong>.</p>
        `,
      },
      {
        id: 'navigating',
        title: 'Finding your way around',
        body: `
          <p>The blue top bar is your home base on every page.</p>
          <ol>
            <li><strong>Brand logo</strong> — returns you to your role's landing dashboard.</li>
            <li style="margin-top:8px;"><strong>Menu groups</strong> — grouped by function (e.g. <strong>Native WMS</strong>, <strong>COD Reconciliation</strong>); hover or click to see the pages inside.</li>
            <li style="margin-top:8px;"><strong>Avatar menu</strong> (right) — your Profile and Sign out.</li>
            <li style="margin-top:8px;"><strong>? button</strong> — opens this help drawer.</li>
          </ol>
          <p style="margin-top:8px;">Most operational pages are scanner-first terminals or worklists with filters at the top and a table below. Dashboards lead with KPI cards, then a chart, then a table.</p>
        `,
      },
    ],
  },

  {
    id: 'warehouse',
    title: 'Warehouse',
    icon: 'fas fa-warehouse',
    roles: [WAREHOUSE_OPERATOR, OPERATOR_ADMIN],
    topics: [
      {
        id: 'warehouse-overview',
        title: 'Warehouse overview',
        body: `
          <p>The <strong>Warehouse Overview</strong> is your landing dashboard. It summarises the day's workload at a glance:</p>
          <ul>
            <li><strong>Orders to Fulfil</strong> — orders waiting to be picked/packed.</li>
            <li><strong>Active Waves</strong> — picking waves in progress.</li>
            <li><strong>Lines Picked</strong> — pick progress across all waves.</li>
            <li><strong>Inbound POs</strong> — purchase orders awaiting receiving.</li>
          </ul>
          <p style="margin-top:8px;">The <strong>Orders by Status</strong> chart and the <strong>Fulfilment Queue</strong> table show where work is piling up. Use <strong>Order Queue</strong> to jump straight into processing.</p>
        `,
      },
      {
        id: 'inbound-receiving',
        title: 'Inbound receiving & QA',
        body: `
          <p>Receive incoming vendor shipments against a Purchase Order on the <strong>Inbound PO Receiving &amp; QA Terminal</strong>.</p>
          <ol>
            <li><strong>Select a PO</strong> from the dropdown.</li>
            <li style="margin-top:8px;"><strong>Scan each item</strong> (or enter counts) to tally received quantity.</li>
            <li style="margin-top:8px;"><strong>Flag damage</strong> — mark defective units, which routes them to <span style="color:#f59e0b;">Quarantine</span>, and complete the QA checklist.</li>
            <li style="margin-top:8px;"><strong>Record batch/lot &amp; expiry</strong> for lot-controlled items.</li>
            <li style="margin-top:8px;"><strong>Print labels</strong> and <strong>Post Reconciliation</strong> to update stock.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">The PO variance panel shows Ordered vs Received vs Damaged so discrepancies are caught at the dock.</p>
        `,
      },
      {
        id: 'directed-putaway',
        title: 'Directed putaway',
        body: `
          <p>After receiving, the system tells you exactly where to store stock on the <strong>Directed Putaway</strong> monitor — fast-movers near pick faces, hazards in designated bins.</p>
          <p style="margin-top:8px;">Each task uses <strong>double-scan verification</strong>:</p>
          <ol>
            <li><strong>Scan the source</strong> cart/pallet.</li>
            <li style="margin-top:8px;"><strong>Scan the item</strong> to confirm the SKU.</li>
            <li style="margin-top:8px;"><strong>Scan the destination bin</strong> the system suggested.</li>
          </ol>
          <p style="margin-top:8px;">If the suggested bin is full, override and pick another bin with capacity — you'll be asked for a reason code so the audit trail stays clean.</p>
        `,
      },
      {
        id: 'picking',
        title: 'Pick queue & waves',
        body: `
          <p>Picking has two screens that work together.</p>
          <p style="margin-top:12px;"><strong>Wave &amp; Batch Picking Console:</strong></p>
          <p>Group pending orders into a wave by carrier, zone, or priority, assign it to a picker, and release it. The progress board shows lines picked vs remaining in real time.</p>
          <p style="margin-top:12px;"><strong>Pick Queue Execution:</strong></p>
          <p>The handheld-style terminal walks the picker bin-by-bin in optimal order. Scan the bin, scan the SKU, enter the count. Use <strong>Flag Short Pick</strong> or <strong>Flag Damaged Stock</strong> for exceptions — both log automatically and adjust stock. When every line is verified the list moves to the packing queue.</p>
        `,
      },
      {
        id: 'packing',
        title: 'Packing station',
        body: `
          <p>The <strong>Packing Station Terminal</strong> prevents mis-packs through scan-to-verify.</p>
          <ol>
            <li><strong>Scan the tote</strong> (e.g. TOTE-101) to load the order's items.</li>
            <li style="margin-top:8px;"><strong>Scan each item</strong> as you place it in the carton — a wrong SKU is rejected with an <span style="color:#ef4444;">Invalid Item</span> warning.</li>
            <li style="margin-top:8px;"><strong>Select a box</strong> and enter weight + dimensions; volumetric weight is auto-calculated.</li>
            <li style="margin-top:8px;"><strong>Confirm &amp; Print Waybill</strong> — the order moves to <span style="color:#22c55e;">Packed / Ready to Ship</span> and tracking is pushed to the marketplace.</li>
          </ol>
        `,
      },
      {
        id: 'inventory-moves',
        title: 'Replenishment, counts & transfers',
        body: `
          <p>Three tools keep stock accurate and pick faces full.</p>
          <p style="margin-top:12px;"><strong>Replenishment Planner:</strong> when a pick bin drops below its min, the system generates a task to top it up to max from bulk storage (FIFO/FEFO). Claim it, scan source → item → destination.</p>
          <p style="margin-top:12px;"><strong>Cycle Counting Console:</strong> work an assigned count sheet with optional <em>blind counts</em>. Variances are computed automatically and routed to a manager for approval before the ledger updates.</p>
          <p style="margin-top:12px;"><strong>Bin Transfer Terminal:</strong> move stock bin-to-bin or make ad-hoc adjustments with a mandatory reason code. Adjustments above the supervisor threshold are held for approval.</p>
        `,
      },
    ],
  },

  {
    id: 'finance',
    title: 'Finance & COD',
    icon: 'fas fa-coins',
    roles: [FINANCE_USER, OPERATOR_ADMIN],
    topics: [
      {
        id: 'finance-overview',
        title: 'Finance overview',
        body: `
          <p>The <strong>Finance Overview</strong> dashboard surfaces money that needs attention: open discrepancies, total COD variance, invoices issued, and RTS claims that are claim-eligible.</p>
          <p style="margin-top:8px;">The <strong>COD Variance by Courier</strong> chart shows which carrier is short-paying, and the <strong>Flagged Discrepancies</strong> table lets you jump into the worklist. Start your day here, then drill into the specific tool.</p>
        `,
      },
      {
        id: 'ingest-remittance',
        title: 'Ingest courier remittance',
        body: `
          <p>Couriers send COD payouts as spreadsheets. Load them on the <strong>COD Remittance Uploader</strong>.</p>
          <ol>
            <li><strong>Pick the courier</strong> and enter a unique batch reference.</li>
            <li style="margin-top:8px;"><strong>Upload</strong> the CSV/XLSX file.</li>
            <li style="margin-top:8px;"><strong>Map columns</strong> — match the file's columns to Waybill, COD Collected, and Fees.</li>
            <li style="margin-top:8px;"><strong>Run Reconciliation</strong> — the engine compares expected COD against what was remitted.</li>
          </ol>
          <p style="margin-top:8px;">The formula: <em>Reconciliation Balance = Expected COD − (Actual COD + Courier Fee)</em>. Mismatches are flagged for the discrepancy worklist.</p>
        `,
      },
      {
        id: 'reconcile-discrepancies',
        title: 'Reconcile discrepancies',
        body: `
          <p>The <strong>COD Discrepancy Reconciliation Worklist</strong> lists flagged payout variances — cash shortages, unremitted amounts, late remittances, and fee overcharges.</p>
          <ol>
            <li><strong>Investigate</strong> a row to see the full transaction ledger (expected vs remitted).</li>
            <li style="margin-top:8px;"><strong>Choose an action</strong> — File Courier Dispute, Escalate, or Accept Variance.</li>
            <li style="margin-top:8px;"><strong>Add notes</strong> (required) and submit; the status moves to <span style="color:#f59e0b;">Disputed</span> with a timestamped audit entry.</li>
          </ol>
          <p style="margin-top:8px;">Export selected rows to a dispute CSV to send to the carrier.</p>
        `,
      },
      {
        id: 'billing-rules',
        title: 'Manage billing rules',
        body: `
          <p>Each client has its own 3PL tariff. On the <strong>Client Billing Rule Configurator</strong> you set per-client rates for storage (per CBM/day), inbound/outbound handling, pick &amp; pack, and COD processing fees.</p>
          <p style="margin-top:8px;">Rates must be non-negative and apply immediately to subsequent billing runs. These rates feed directly into invoice generation, so keep them current with each client contract.</p>
        `,
      },
      {
        id: 'invoices',
        title: 'Generate e-invoices (BIR)',
        body: `
          <p>The <strong>Billing &amp; e-Invoices Gate</strong> aggregates a client's monthly operations into a draft invoice, then validates it for BIR EIS compliance before issuing.</p>
          <ol>
            <li><strong>Generate Draft</strong> for a client and month — line items are priced from that client's billing rules.</li>
            <li style="margin-top:8px;"><strong>Trigger Compliance Check</strong> — a GraphIQ validation verdict.</li>
            <li style="margin-top:8px;"><strong>Finalize &amp; Issue</strong> — this is <em>hard-gated</em>: if validation returns <span style="color:#ef4444;">Failed-Closed</span> (e.g. missing BIR tax ID), the issue button stays locked.</li>
          </ol>
          <p style="margin-top:8px;">On success the invoice moves to <span style="color:#22c55e;">Sent</span> and is recorded in the compliance audit ledger.</p>
        `,
      },
      {
        id: 'rts-claims',
        title: 'RTS & damage claims',
        body: `
          <p>The <strong>Courier RTS &amp; Transit Damage Claims Ledger</strong> tracks failed deliveries (Return-to-Sender) and in-transit damage so you can recover money from carriers.</p>
          <ul>
            <li>Parcels past the courier SLA (e.g. 14 days no update) are auto-flagged <span style="color:#ef4444;">SLA Fail — Claim Eligible</span>.</li>
            <li><strong>File a claim</strong> with proof photos; the amount is auto-calculated as <em>min(item cost, carrier liability cap)</em>.</li>
            <li>Track the lifecycle: Pending Carrier Review → Approved → Recovered.</li>
            <li>On recovery, a merchant credit note is generated automatically.</li>
          </ul>
        `,
      },
      {
        id: 'tax-currency',
        title: 'Multi-currency & tax',
        body: `
          <p>The <strong>Regional Tax &amp; Currency Manager</strong> keeps cross-market billing accurate.</p>
          <p style="margin-top:8px;">Add exchange-rate rows manually or enable automatic API sync; define country tax rules (VAT/GST) with effective dates; and assign invoice templates per country. Tax codes must be unique per country, and you'll be warned before overriding historical rates that invoices already reference.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Line total = Base × Exchange Rate × (1 + Tax %).</p>
        `,
      },
    ],
  },

  {
    id: 'merchant',
    title: 'Merchant Portal',
    icon: 'fas fa-store',
    roles: [MERCHANT_USER, OPERATOR_ADMIN],
    topics: [
      {
        id: 'analytics-dashboard',
        title: 'Analytics dashboard',
        body: `
          <p>Your <strong>Client Analytics Dashboard</strong> is the command centre for your store's fulfilment health. KPI cards show Expected vs Remitted COD and Unpaid Invoices; charts cover daily outbound velocity and inventory allocation; and a Stock Alerts widget flags SKUs below threshold.</p>
          <p style="margin-top:8px;">All data is strictly scoped to your account — you never see another merchant's figures.</p>
        `,
      },
      {
        id: 'order-dashboard',
        title: 'Order dashboard',
        body: `
          <p>The <strong>Merchant Orders Dashboard</strong> lets you search and filter your orders by status, channel, and date, and follow each one through its delivery milestones (Ingested → Packed → Shipped → Delivered → RTS).</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Customer name, phone, and address are masked by default to protect PII per NPC privacy rules.</p>
        `,
      },
      {
        id: 'cod-collections',
        title: 'COD collections',
        body: `
          <p><strong>Merchant COD Collections</strong> shows your cash flow: expected COD, actual remittances received, courier fee deductions, and net settlement.</p>
          <p style="margin-top:8px;">Charts trend daily collections; the ledger lists each remittance batch. Click a batch to inspect the individual waybills, delivery dates, and payouts, or export to Excel.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Net Settlement = Gross COD Collected − Total Courier Fees.</p>
        `,
      },
      {
        id: 'regional-performance',
        title: 'Regional performance',
        body: `
          <p><strong>Regional Merchant Performance</strong> compares your markets side by side — revenue, order volume, SLA compliance, and RTS rates per SEA country.</p>
          <p style="margin-top:8px;">Toggle the currency between your billing currency (USD) and local currencies (PHP/IDR/VND) with live FX, filter by date range and country, and export the comparison to CSV or PDF.</p>
        `,
      },
      {
        id: 'connect-store',
        title: 'Connect a store',
        body: `
          <p>Link your marketplace stores from the <strong>Channel Integration Catalog</strong> so LogiOS can pull orders and sync inventory.</p>
          <ol>
            <li><strong>Click Connect</strong> on Shopee, Lazada, TikTok Shop, or Shopify.</li>
            <li style="margin-top:8px;"><strong>Authorise</strong> on the marketplace's secure OAuth page.</li>
            <li style="margin-top:8px;"><strong>Confirm</strong> — you're returned to LogiOS and the store shows as <span style="color:#22c55e;">Active</span> with a token expiry date.</li>
          </ol>
          <p style="margin-top:8px;">You can renew the token or disconnect a store at any time from the same screen.</p>
        `,
      },
    ],
  },

  {
    id: 'crossborder',
    title: 'Cross-Border',
    icon: 'fas fa-earth-asia',
    roles: [CROSS_BORDER_OPS, OPERATOR_ADMIN],
    topics: [
      {
        id: 'cb-exceptions',
        title: 'Shipment exceptions',
        body: `
          <p>The <strong>Cross-Border Logistics Dashboard</strong> tracks international shipments leg by leg (Origin → Port/Border → Destination Hub → Last Mile) and highlights anything stuck.</p>
          <p style="margin-top:8px;">KPI cards count shipments with exceptions, customs holds, and merchant-action items. Filter the ledger by customs status to focus on what's blocked, then open a shipment to act on it.</p>
        `,
      },
      {
        id: 'cb-customs',
        title: 'Customs docs & HS codes',
        body: `
          <p>Open a flagged shipment's side panel to resolve it.</p>
          <ul>
            <li><strong>Upload missing documents</strong> — commercial invoice, certificate of origin, packing list — straight into the document vault; the status updates to Customs Review Pending.</li>
            <li><strong>Map HS codes</strong> — assign a Harmonized System tariff code to clear a "Missing HS Code" flag (or use Auto-Repair).</li>
            <li><strong>Generate a customs PDF</strong> dossier for the shipment.</li>
          </ul>
          <p style="margin-top:8px;">When a shipment needs the merchant to act, an automated email notification is dispatched.</p>
        `,
      },
    ],
  },

  {
    id: 'regional',
    title: 'Regional Admin',
    icon: 'fas fa-map-location-dot',
    roles: [REGIONAL_ADMIN, OPERATOR_ADMIN],
    topics: [
      {
        id: 'regional-overview',
        title: 'Regional overview',
        body: `
          <p>The <strong>Regional Operations Overview</strong> summarises your SEA market configuration: countries configured, active markets, distinct currencies, and tax regimes, plus an address-hierarchy depth chart and the full country profile table.</p>
          <p style="margin-top:8px;">It's the quickest way to confirm every market you operate in is set up correctly before orders start flowing.</p>
        `,
      },
      {
        id: 'country-profiles',
        title: 'Country operational profiles',
        body: `
          <p>On <strong>Country Operations Profile</strong> you create and manage per-country settings that the rest of the platform depends on:</p>
          <ul>
            <li><strong>Default currency</strong> for pricing and billing.</li>
            <li><strong>Local tax rules</strong> (e.g. 12% VAT).</li>
            <li><strong>Address hierarchy</strong> (e.g. Province → City/Municipality → Barangay) used for validation and shipping labels.</li>
          </ul>
          <p style="margin-top:8px;">These settings are applied dynamically during invoice generation and label creation, so accurate profiles mean fewer downstream errors.</p>
        `,
      },
    ],
  },

  {
    id: 'admin',
    title: 'Operator Admin',
    icon: 'fas fa-user-shield',
    roles: [OPERATOR_ADMIN],
    topics: [
      {
        id: 'order-routing',
        title: 'Order routing rules',
        body: `
          <p>On <strong>Order Routing Rules</strong> you map incoming marketplace orders to the right warehouse and courier automatically, based on client, region, and priority.</p>
          <p style="margin-top:8px;">Rules run in priority order (lowest number first). Give each rule a name, target client/region/warehouse/courier, and a priority from 1–100. Validation prevents empty or negative values.</p>
        `,
      },
      {
        id: 'courier-config',
        title: 'Courier configurations',
        body: `
          <p><strong>Courier Settings &amp; Service Zones</strong> is where you onboard carriers (Ninja Van, J&amp;T, Flash, LBC, Entrego).</p>
          <ol>
            <li><strong>Enter credentials</strong> (URL, key, client ID) and choose Sandbox or Production.</li>
            <li style="margin-top:8px;"><strong>Test Connection</strong> to verify connectivity.</li>
            <li style="margin-top:8px;"><strong>Configure rates</strong> — base, zone, and surcharge. Rates must be non-negative.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Total Shipping = Base + (Excess Weight × Rate) + Zone Surcharge.</p>
        `,
      },
      {
        id: 'channel-catalog',
        title: 'Channel integration catalog',
        body: `
          <p>Configure the developer credentials for each marketplace connector on the <strong>Channel Integration Catalog</strong>.</p>
          <p style="margin-top:8px;">Enter App Key, App Secret, and Partner ID — stored with AES-256 encryption — and the system generates a unique, secure webhook endpoint per connector to receive live order events. Mandatory fields are validated before saving.</p>
        `,
      },
      {
        id: 'sync-monitor',
        title: 'Integration sync monitor',
        body: `
          <p>The <strong>Integration Sync &amp; API Monitor</strong> is your health panel for all marketplace connections. KPI cards track total sync attempts, success rate, failed webhook ingests, and expiring tokens.</p>
          <p style="margin-top:8px;">The searchable sync log records every transaction (order ingestion, inventory update, waybill pull). Click <strong>Inspect</strong> to see the raw payload and error, then <strong>Retry Sync</strong> to re-queue a failed transaction. An automated retry policy with exponential backoff handles transient failures.</p>
        `,
      },
      {
        id: 'warehouse-config',
        title: 'Warehouse layout & occupancy',
        body: `
          <p>Two admin tools define and monitor your physical warehouse.</p>
          <p style="margin-top:12px;"><strong>Location Designer &amp; Bin Configurator:</strong> build the storage hierarchy (Zone → Aisle → Rack → Shelf → Bin), batch-generate coordinates, and set each bin's capacity (CBM/weight), storage type, and restrictions (merchant-lock, SKU category, pick speed).</p>
          <p style="margin-top:12px;"><strong>Storage Occupancy Monitor:</strong> see real-time utilisation per bin, filter by status, and override a location's status (Block, Quarantine) to take it out of putaway/picking — useful for damaged racks or QA holds.</p>
        `,
      },
    ],
  },

  {
    id: 'guide',
    title: 'How To',
    icon: 'fas fa-book-open',
    roles: null,
    topics: [
      {
        id: 'fulfil-order',
        title: 'Fulfil an order end-to-end',
        body: `
          <p>The happy path from marketplace order to shipped parcel:</p>
          <ol>
            <li><strong>Pick</strong> — release a wave, then work the Pick Queue: scan bin → SKU → count into a tote.</li>
            <li style="margin-top:8px;"><strong>Pack</strong> — at the Packing Station, scan the tote, scan each item, select a box, capture weight/dimensions.</li>
            <li style="margin-top:8px;"><strong>Ship</strong> — Confirm &amp; Print Waybill. The order becomes <span style="color:#22c55e;">Ready to Ship</span> and tracking is pushed to the channel.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Roles: Warehouse Operator (and Operator Admin).</p>
        `,
      },
      {
        id: 'receive-po',
        title: 'Receive a purchase order',
        body: `
          <ol>
            <li>Open <strong>Inbound PO Receiving &amp; QA</strong> and select the approved PO.</li>
            <li style="margin-top:8px;">Scan items to count; flag any damaged units to Quarantine and complete the QA checklist.</li>
            <li style="margin-top:8px;">Record batch/lot &amp; expiry where required, then print barcode labels.</li>
            <li style="margin-top:8px;"><strong>Post Reconciliation</strong> to update stock; received goods then appear as Directed Putaway tasks.</li>
          </ol>
        `,
      },
      {
        id: 'reconcile-cod',
        title: 'Reconcile a COD batch',
        body: `
          <ol>
            <li>On <strong>COD Remittance Uploader</strong>, choose the courier, enter a batch reference, and upload the payout file.</li>
            <li style="margin-top:8px;">Map the columns and Run Reconciliation.</li>
            <li style="margin-top:8px;">Review flagged variances in the <strong>Discrepancy Worklist</strong>; investigate, add notes, and file a courier dispute or accept the variance.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Roles: Finance User (and Operator Admin).</p>
        `,
      },
      {
        id: 'issue-invoice',
        title: 'Issue a client invoice',
        body: `
          <ol>
            <li>Confirm the client's rates on <strong>Billing Rule Configurator</strong>.</li>
            <li style="margin-top:8px;">On <strong>Billing &amp; e-Invoices Gate</strong>, Generate Draft for the client and month.</li>
            <li style="margin-top:8px;">Run the GraphIQ compliance check.</li>
            <li style="margin-top:8px;">If it passes, Finalize &amp; Issue. A Failed-Closed verdict locks issuing until you fix the flagged item (e.g. add the BIR tax ID).</li>
          </ol>
        `,
      },
      {
        id: 'connect-marketplace',
        title: 'Connect a marketplace',
        body: `
          <p><strong>Merchants:</strong> open the Channel Integration Catalog, click Connect on your platform, authorise via OAuth, and confirm the store shows Active.</p>
          <p style="margin-top:8px;"><strong>Operator Admins:</strong> first ensure the connector's developer credentials (App Key/Secret/Partner ID) are configured in the catalog, then merchants can self-connect. Monitor health afterwards on the Sync Monitor.</p>
        `,
      },
    ],
  },

  {
    id: 'faq',
    title: 'FAQ',
    icon: 'fas fa-circle-question',
    roles: null,
    topics: [
      {
        id: 'faq-access',
        title: 'Why can’t I see a page?',
        body: `
          <p>LogiOS only shows the modules your role is permitted to use, so if a menu or page isn't visible, your role doesn't have access. If you follow an old bookmark to a restricted page, the app returns you to your own dashboard instead of showing an error.</p>
          <p style="margin-top:8px;">If you believe you need access to something, contact your Operator Admin to confirm your role.</p>
        `,
      },
      {
        id: 'faq-demo',
        title: 'How do I sign in?',
        body: `
          <p>Use your email and password on the sign-in screen. In development environments, one-click demo buttons under "Demo accounts" let you sign in as each role instantly.</p>
          <p style="margin-top:8px;">After signing in you always land on the dashboard built for your role. Use the avatar menu → Sign out to switch accounts.</p>
        `,
      },
      {
        id: 'faq-data',
        title: 'Why is my data masked / limited?',
        body: `
          <p>Two protections are always on:</p>
          <ul>
            <li><strong>PII masking</strong> — customer names, phones, and addresses are masked by default to comply with NPC privacy rules.</li>
            <li><strong>Tenant isolation</strong> — Merchant Users only ever see their own store's orders, COD, and analytics; no competitor data is shown.</li>
          </ul>
        `,
      },
      {
        id: 'faq-scan',
        title: 'A scan was rejected — what now?',
        body: `
          <p>Scanners verify against the active task, so a rejection usually means the item isn't part of this order/bin, or it's already fully scanned (over-scan).</p>
          <ul>
            <li><strong>Packing</strong> — an Invalid Item warning means the SKU isn't on the order; set it aside.</li>
            <li><strong>Picking</strong> — use Flag Short Pick or Flag Damaged Stock to log a genuine exception.</li>
          </ul>
        `,
      },
      {
        id: 'faq-cod',
        title: 'What does a COD discrepancy mean?',
        body: `
          <p>It means the cash a courier remitted doesn't match what was expected for those deliveries. Common types are cash shortage, unremitted, late remittance, and fee overcharge.</p>
          <p style="margin-top:8px;">Investigate the row in the Discrepancy Worklist, then file a courier dispute (export the CSV) or accept the variance with a note.</p>
        `,
      },
      {
        id: 'faq-support',
        title: 'Something looks wrong — who do I tell?',
        body: `
          <p>For integration/sync failures, an Operator Admin can inspect and retry transactions on the Sync Monitor. For courier payout or claim issues, raise it with Finance. For access or configuration questions, contact your Operator Admin.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: note the reference shown on screen (waybill, batch, sync ID) — it speeds up any follow-up.</p>
        `,
      },
    ],
  },
];

/** Sections visible to a given role (null `roles` = everyone). */
export function sectionsForRole(role) {
  return helpSections.filter((s) => !s.roles || (role && s.roles.includes(role)));
}

/** Map the current route to the most relevant help section id. */
export function getDefaultSection(pathname) {
  const p = pathname || '';
  if (/(inbound|putaway|pick|pack|wave|replenish|cycle-count|bin-transfer|warehouse-overview|warehouse-orders)/.test(p)) return 'warehouse';
  if (/(cod|remittance|discrepancy|billing|invoice|rts|tax-and-currency|finance-overview)/.test(p)) return 'finance';
  if (/(merchant-orders|merchant-cod|client-analytics|regional-merchant)/.test(p)) return 'merchant';
  if (/(cross-border)/.test(p)) return 'crossborder';
  if (/(country-operations|regional-overview)/.test(p)) return 'regional';
  if (/(routing|courier-settings|channel-integration|integration-sync|location-designer|occupancy)/.test(p)) return 'admin';
  return 'welcome';
}

/** Map the current route to a sensible default topic within its section. */
export function getDefaultTopic(pathname) {
  const p = pathname || '';
  const map = [
    ['inbound', 'inbound-receiving'], ['putaway', 'directed-putaway'],
    ['pick', 'picking'], ['wave', 'picking'], ['packing', 'packing'],
    ['replenish', 'inventory-moves'], ['cycle-count', 'inventory-moves'], ['bin-transfer', 'inventory-moves'],
    ['warehouse-overview', 'warehouse-overview'],
    ['remittance', 'ingest-remittance'], ['discrepancy', 'reconcile-discrepancies'],
    ['billing', 'billing-rules'], ['invoice', 'invoices'], ['rts', 'rts-claims'],
    ['tax-and-currency', 'tax-currency'], ['finance-overview', 'finance-overview'],
    ['client-analytics', 'analytics-dashboard'], ['merchant-orders', 'order-dashboard'],
    ['merchant-cod', 'cod-collections'], ['regional-merchant', 'regional-performance'],
    ['cross-border', 'cb-exceptions'],
    ['country-operations', 'country-profiles'], ['regional-overview', 'regional-overview'],
    ['routing', 'order-routing'], ['courier-settings', 'courier-config'],
    ['channel-integration', 'channel-catalog'], ['integration-sync', 'sync-monitor'],
    ['location-designer', 'warehouse-config'], ['occupancy', 'warehouse-config'],
  ];
  for (const [k, v] of map) if (p.includes(k)) return v;
  return 'getting-started';
}
